import { useEffect, useState } from "react";

const HIGHLIGHT_MS = 2400;

/**
 * Reliable cold-load + in-page hash routing for SPA pages.
 *
 * - Scrolls to the hash target after first paint (so the element exists in
 *   the DOM by the time we ask the browser to scroll).
 * - Also listens for `hashchange` events so in-page anchor clicks trigger the
 *   same scroll + highlight behavior, not just full-page loads.
 * - Returns the active hash id while a highlight should be displayed
 *   (2.4s after the most recent change), so pages can render a pulse / ring
 *   on the matching card. Returns `null` when no highlight is active.
 *
 * Usage:
 *   const highlighted = useHashScroll();
 *   <Card className={highlighted === "sec-contracts" ? "ring-2 ring-violet-500" : ""} />
 *
 * Pass `deps` if the target may not exist until some async data has loaded
 * (e.g. articles list). The hook will re-attempt scroll/highlight whenever a
 * dep changes.
 */
export function useHashScroll(deps = []) {
  const [highlighted, setHighlighted] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    let raf = null;
    let timer = null;

    const trigger = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const id = hash.slice(1);
      raf = requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setHighlighted(id);
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => setHighlighted(null), HIGHLIGHT_MS);
      });
    };

    trigger();
    window.addEventListener("hashchange", trigger);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
      window.removeEventListener("hashchange", trigger);
    };
  }, deps);

  return highlighted;
}
